import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const SKELETON_ROWS = ["alpha", "bravo", "charlie"];

type MaterialResellerEntry = {
	id?: string | number | null;
	description?: string | null;
	brand?: string | null;
	model?: string | null;
	price?: number | null;
	newStock?: number | null;
	usedStock?: number | null;
	stockMin?: number | null;
	requestedQuantity?: number | null;
};

type MaterialListResellerTableProps = {
	materials?: MaterialResellerEntry[];
	loading?: boolean;
	resellerCommission?: number;
};

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

const buildDescription = (entry: MaterialResellerEntry) => {
	const description = entry.description?.trim() ?? "";
	const brand = entry.brand?.trim() ?? "";
	const model = entry.model?.trim() ?? "";
	return `${description} , ${brand} ${model}`.trim();
};

const resolveStockTone = (entry: MaterialResellerEntry) => {
	const newStock = entry.newStock ?? 0;
	const usedStock = entry.usedStock ?? 0;
	const totalStock = newStock + usedStock;
	const minStock = entry.stockMin ?? 0;

	if (totalStock === 0) {
		return "bg-destructive/15 text-destructive";
	}
	if (totalStock <= minStock) {
		return "bg-amber-100 text-amber-700";
	}
	return "bg-muted text-foreground";
};

export function MaterialListResellerTable({
	materials = [],
	loading = false,
	resellerCommission = 0,
}: MaterialListResellerTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`reseller-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-48 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-12 rounded bg-muted/40" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead>Descrição</TableHead>
						<TableHead className="w-[90px] text-center">Preço</TableHead>
						<TableHead className="w-[150px] text-center">
							Preço c/ IVA
						</TableHead>
						<TableHead className="w-[100px] text-center">Stock</TableHead>
						<TableHead className="w-[100px] text-center">Aguarda</TableHead>
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : (
					<TableBody>
						{materials.map((material) => {
							const rowKey = material.id ?? material.description ?? "material";
							const basePrice = normalizeNumber(material.price);
							const price =
								basePrice > 0 ? basePrice + resellerCommission : basePrice;
							const priceWithTax = price > 0 ? price * 1.23 : price;
							const stockLabel = `${material.newStock ?? 0}/${
								material.usedStock ?? 0
							}`;

							return (
								<TableRow key={rowKey}>
									<TableCell>{buildDescription(material)}</TableCell>
									<TableCell className="text-center">
										{formatCurrency(price)}
									</TableCell>
									<TableCell className="text-center">
										{formatCurrency(priceWithTax)}
									</TableCell>
									<TableCell className="text-center">
										<span
											className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${resolveStockTone(
												material,
											)}`}
										>
											{stockLabel}
										</span>
									</TableCell>
									<TableCell className="text-center">
										{material.requestedQuantity ?? 0}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				)}
			</Table>
		</div>
	);
}
